
import React, { useMemo, useState, useEffect, useRef } from "react";
import axios from "axios";
import style from "./Edit.module.css";
import PageLayout from "../../components/PageLayout/PageLayout";
import { Form, Formik } from "formik";
import FormikField from "../../stuffs/FormikField/FormikField";
import { Button, FormControlLabel, Switch } from "@mui/material";
import {
  CompanyType,
  editCompany,
  EditCompany,
  selectCompanies,
  updateCompanyImages,
} from "../../slices/company/companySlice";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useHistory, useParams } from "react-router-dom";

// Pakiet wyliczany z dat ważności zapisanych dla konkretnej firmy.
type PackageCode = "free" | "extended" | "featured" | "top3";
type CompanyPackageDates = {
  expirationDate?: string | Date | null;
  highlightedExpirationDate?: string | Date | null;
  recommendedExpirationDate?: string | Date | null;
};

const PACKAGE_RULES: Record<PackageCode, { label: string; photos: number; logo: boolean }> = {
  free: { label: "Darmowy", photos: 0, logo: false },
  extended: { label: "Rozszerzony", photos: 1, logo: true },
  featured: { label: "Wyróżniony", photos: 3, logo: true },
  top3: { label: "TOP 3", photos: 5, logo: true },
};

const isActive = (date: string | Date | null | undefined, now: number): boolean => {
  if (!date) return false;
  const timestamp = new Date(date).getTime();
  return Number.isFinite(timestamp) && timestamp > now;
};

const resolvePackage = (company: CompanyPackageDates | undefined, now: number): PackageCode => {
  if (!company) return "free";
  const extended = isActive(company.expirationDate, now);
  const featured = isActive(company.highlightedExpirationDate, now);
  const top3 = isActive(company.recommendedExpirationDate, now);

  if (extended && featured && top3) return "top3";
  if (extended && featured) return "featured";
  if (extended) return "extended";
  return "free";
};

interface selectValuesAndLabels {
  value: string | number;
  label: string;
}

const Edit: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const companies = useSelector(selectCompanies);
  const { id } = useParams<{ id: string }>();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoMessage, setLogoMessage] = useState("");
  const logoInputRef = useRef<HTMLInputElement>(null);

  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [orderedPhotos, setOrderedPhotos] = useState<string[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [photoMessage, setPhotoMessage] = useState("");
  const photoInputRef = useRef<HTMLInputElement>(null);




  // Znajdź firmę po ID z URL
  const company = useMemo(
    () => companies.find((c) => String(c._id) === String(id)),
    [companies, id]
  ) as CompanyType | undefined;

  // Aktualizujemy podgląd, gdy Redux dostarczy dane firmy.
  useEffect(() => {
    setLogoUrl(company?.images?.logo || null);
    setLogoFile(null);
    setLogoMessage("");
  }, [company?._id, company?.images?.logo]);

  const packageCode = resolvePackage(
    company as (CompanyType & CompanyPackageDates) | undefined,
    Date.now()
  );
  const packageRules = PACKAGE_RULES[packageCode];
  const visibleLimit = packageRules.photos;
  // Galeria jest renderowana ze stanu lokalnego, aby strzałki
  // natychmiast zmieniały widoczną kolejność.
  const photos = orderedPhotos;

  useEffect(() => {
    setOrderedPhotos(company?.images?.photos || []);
  }, [company?._id, company?.images?.photos]);
  const visibleCount = Math.min(photos.length, visibleLimit);
  const hiddenCount = Math.max(0, photos.length - visibleLimit);

  useEffect(() => {
    setPhotoFiles([]);
    setPhotoMessage("");
    if (photoInputRef.current) photoInputRef.current.value = "";
  }, [company?._id]);

  useEffect(() => {
    const urls = photoFiles.map((file) => URL.createObjectURL(file));
    setPhotoPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [photoFiles]);

  const getImageUrl = (url: string) =>
    url.startsWith("http")
      ? url
      : `${process.env.REACT_APP_UPLOADS_URL || ""}${url}`;

  const choosePhotos = (files: FileList | null) => {
    setPhotoMessage("");
    const selected = Array.from(files || []);
    if (selected.length === 0) {
      setPhotoFiles([]);
      return;
    }
    if (selected.length + photos.length > 5) {
      setPhotoFiles([]);
      if (photoInputRef.current) photoInputRef.current.value = "";
      setPhotoMessage(`Możesz dodać jeszcze maksymalnie ${5 - photos.length} zdjęć.`);
      return;
    }
    if (selected.some((file) =>
      !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
      file.size > 10 * 1024 * 1024
    )) {
      setPhotoFiles([]);
      if (photoInputRef.current) photoInputRef.current.value = "";
      setPhotoMessage("Każde zdjęcie musi być JPG, PNG lub WebP i mieć maksymalnie 10 MB.");
      return;
    }
    setPhotoFiles(selected);
  };

  const savePhotos = async () => {
    if (!company?._id || !photoFiles.length || photoBusy) return;
    setPhotoBusy(true);
    setPhotoMessage("");
    try {
      const data = new FormData();
      photoFiles.forEach((file) => data.append("photos", file));
      const response = await axios.post(`/api/offers/${company._id}/photos`, data, {
        headers: { "x-auth-token": localStorage.getItem("token") || "" },
      });
      dispatch(updateCompanyImages({ id: company._id, images: response.data.images }));
      setOrderedPhotos(response.data.images.photos || []);
      setPhotoFiles([]);
      if (photoInputRef.current) photoInputRef.current.value = "";
      setPhotoMessage("Zdjęcia zostały zapisane.");
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setPhotoMessage(error.response?.data?.message || "Nie udało się zapisać zdjęć.");
      } else {
        setPhotoMessage("Nie udało się zapisać zdjęć.");
      }
    } finally {
      setPhotoBusy(false);
    }
  };

  const movePhoto = async (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (!company?._id || photoBusy || reordering ||
        nextIndex < 0 || nextIndex >= photos.length) return;

    const previous = [...photos];
    const reordered = [...previous];
    [reordered[index], reordered[nextIndex]] =
      [reordered[nextIndex], reordered[index]];

    // Natychmiast pokaż zmianę, jeszcze przed odpowiedzią serwera.
    setOrderedPhotos(reordered);
    setReordering(true);
    setPhotoMessage("Zapisywanie nowej kolejności...");

    try {
      const response = await axios.patch(
        `/api/offers/${company._id}/photos/order`,
        { photos: reordered },
        { headers: { "x-auth-token": localStorage.getItem("token") || "" } }
      );

      const savedPhotos: string[] | undefined = response.data?.images?.photos;
      if (!Array.isArray(savedPhotos) ||
          savedPhotos.length !== reordered.length ||
          !savedPhotos.every((photo, i) => photo === reordered[i])) {
        throw new Error("Serwer nie potwierdził zapisania nowej kolejności zdjęć.");
      }

      dispatch(updateCompanyImages({ id: company._id, images: response.data.images }));
      setOrderedPhotos(savedPhotos);
      setPhotoMessage("Kolejność zdjęć została zapisana.");
    } catch (error) {
      // Nie udawaj sukcesu: przywróć poprzednią kolejność.
      setOrderedPhotos(previous);
      setPhotoMessage(
        axios.isAxiosError(error)
          ? error.response?.data?.message ||
            `Nie udało się zapisać kolejności${error.response?.status ? ` (HTTP ${error.response.status})` : ""}.`
          : error instanceof Error
            ? error.message
            : "Nie udało się zmienić kolejności zdjęć."
      );
    } finally {
      setReordering(false);
    }
  };

  const deletePhoto = async (photo: string) => {
    if (!company?._id || photoBusy ||
      !window.confirm("Czy na pewno chcesz trwale usunąć to zdjęcie?")) return;
    setPhotoBusy(true);
    setPhotoMessage("");
    try {
      const response = await axios.delete(`/api/offers/${company._id}/photos`, {
        headers: { "x-auth-token": localStorage.getItem("token") || "" },
        data: { photo },
      });
      dispatch(updateCompanyImages({ id: company._id, images: response.data.images }));
      setOrderedPhotos(response.data.images.photos || []);
      setPhotoMessage("Zdjęcie zostało usunięte.");
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setPhotoMessage(error.response?.data?.message || "Nie udało się usunąć zdjęcia.");
      } else {
        setPhotoMessage("Nie udało się usunąć zdjęcia.");
      }
    } finally {
      setPhotoBusy(false);
    }
  };

  // Lokalny podgląd wybranego pliku; zwalniamy adres po zmianie pliku.
  useEffect(() => {
    if (!logoFile) {
      setLogoPreview(null);
      return;
    }
    const previewUrl = URL.createObjectURL(logoFile);
    setLogoPreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [logoFile]);

  const saveLogo = async () => {
    if (!logoFile || !company) return;
    setLogoBusy(true);
    setLogoMessage("");
    try {
      const formData = new FormData();
      formData.append("logo", logoFile);
      const response = await axios.post(
        `/api/offers/${company._id}/logo`,
        formData,
        { headers: { "x-auth-token": localStorage.getItem("token") || "" } }
      );
      setLogoUrl(response.data.logo);
      setLogoFile(null);
      if (logoInputRef.current) logoInputRef.current.value = "";
      setLogoMessage("Logo zostało zapisane.");
    } catch (error) {
      setLogoMessage("Nie udało się zapisać logo. Sprawdź plik i spróbuj ponownie.");
    } finally {
      setLogoBusy(false);
    }
  };

  const deleteLogo = async () => {
    if (!company || !logoUrl || !window.confirm("Czy na pewno chcesz usunąć logo firmy?\nLogo zostanie trwale usunięte z serwera."
)) return;
    setLogoBusy(true);
    setLogoMessage("");
    try {
      await axios.delete(`/api/offers/${company._id}/logo`, {
        headers: { "x-auth-token": localStorage.getItem("token") || "" },
      });
      setLogoUrl(null);
      setLogoFile(null);
      if (logoInputRef.current) logoInputRef.current.value = "";
      setLogoMessage("Logo zostało usunięte.");
    } catch (error) {
      setLogoMessage("Nie udało się usunąć logo.");
    } finally {
      setLogoBusy(false);
    }
  };

  const [vehiclesSet] = useState<selectValuesAndLabels[]>([
    { label: "Cysterna", value: "Tanker" },
    { label: "Plandeka", value: "Tilt" },
    { label: "Kontener", value: "Container" },
    { label: "HDS", value: "LoaderCrane" },
    { label: "Furgon", value: "Bus" },
    { label: "Drobnica", value: "Cargo" },
    { label: "Dźwig", value: "Crane" },
    { label: "Betoniarka", value: "Mixer" },
    { label: "Wywrotka", value: "Tipper" },
    { label: "Koparka", value: "Digger" },
  ]);

  const vehiclesItems: Array<selectValuesAndLabels> = vehiclesSet;

  // Walidacja opisów
  const AddCompanySchema = Yup.object().shape({
    description: Yup.string().max(
      500,
      "Opis działalności może mieć maksymalnie 500 znaków"
    ),
    additionalDescription: Yup.string().max(
      800,
      "Dodatkowy opis może mieć maksymalnie 800 znaków"
    ),
  });

  const handleSubmit = (values: EditCompany): void => {
    try {
      dispatch(editCompany(values));
      history.push("/");
    } catch (e) {
      console.log(e);
    }
  };

  // Jeśli nie znaleziono firmy
  if (!company) {
    return (
      <PageLayout className={style.EditPage}>
        <div className={style.Page}>
          <h2>Nie znaleziono oferty do edycji</h2>
          <p>ID z URL: {id}</p>
          <NavLink to="/my-offers">Wróć do ofert</NavLink>
        </div>
      </PageLayout>
    );
  }

  // Dane początkowe formularza
  const initialValues: EditCompany = {
    _id: company._id,
    user: company.user,
    shortName: company.shortName,
    name: company.name,
    website: company.website,
    email: company.email,
    phone: company.phone,
    NIP: company.NIP,
    description: company.description,
    additionalDescription:
      company.additionalDescription || "",
    isInternational: company.isInternational,
    isADR: company.isADR,
    isOversized: company.isOversized ?? false,
  };

  return (
    <PageLayout className={style.EditPage}>
      <div className={style.Page}>
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={AddCompanySchema}
            enableReinitialize={true}
          >
            {({
              isValid,
              errors,
              touched,
              values,
              setFieldValue,
            }) => (
              <div className={style.Page}>
                <Form>
                  <div className={style.Form}>

                    {/* NAZWA FIRMY */}
                    <div className={style.Row}>
                      <div className={style.Field}>
                        <FormikField
                          name="shortName"
                          value={values.shortName}
                          label="Nazwa Skrócona"
                          variant="standard"
                          err={
                            errors.shortName &&
                            touched.shortName
                          }
                        />
                      </div>

                      <div className={style.Field}>
                        <FormikField
                          name="name"
                          value={values.name}
                          label="Nazwa"
                          variant="standard"
                          err={
                            errors.name &&
                            touched.name
                          }
                        />
                      </div>
                    </div>

                    {/* KONTAKT */}
                    <div className={style.Row}>
                      <div className={style.Field}>
                        <FormikField
                          name="email"
                          value={values.email}
                          label="Email"
                          variant="standard"
                          err={
                            errors.email &&
                            touched.email
                          }
                        />
                      </div>

                      <div className={style.Field}>
                        <FormikField
                          name="website"
                          value={values.website}
                          label="Strona internetowa"
                          variant="standard"
                          err={
                            errors.website &&
                            touched.website
                          }
                        />
                      </div>
                    </div>

                    {/* TELEFON I NIP */}
                    <div className={style.Row}>
                      <div className={style.Field}>
                        <FormikField
                          name="phone"
                          value={values.phone}
                          label="Telefon"
                          variant="standard"
                          required
                          err={
                            errors.phone &&
                            touched.phone
                          }
                        />
                      </div>

                      <div className={style.Field}>
                        <FormikField
                          name="NIP"
                          value={values.NIP}
                          label="NIP"
                          variant="standard"
                          required
                          err={
                            errors.NIP &&
                            touched.NIP
                          }
                        />
                      </div>
                    </div>

                    {/* OPIS DZIAŁALNOŚCI */}
                    <div className={`${style.Row} ${style.DescriptionRow}`}>
                      <div
                        className={style.Field}
                        style={{ width: "100%" }}
                      >
                        <FormikField
                          name="description"
                          value={values.description || ""}
                          label="Opis działalności (maksymalnie 500 znaków)"
                          variant="standard"
                          multiline
                          rows={3}
                          err={
                            errors.description &&
                            touched.description
                          }
                        />
                      </div>
                    </div>

                    {/* DODATKOWY OPIS FIRMY */}
                    <div className={`${style.Row} ${style.DescriptionRow}`}>
                      <div
                        className={style.Field}
                        style={{ width: "100%" }}
                      >
                        <FormikField
                          name="additionalDescription"
                          value={
                            values.additionalDescription || ""
                          }
                          label="Dodatkowy opis firmy (maksymalnie 800 znaków)"
                          variant="standard"
                          multiline
                          rows={4}
                          err={
                            errors.additionalDescription &&
                            touched.additionalDescription
                          }
                        />
                      </div>
                    </div>

                    {/* ADR, TRANSPORT MIĘDZYNARODOWY I PONADNORMATYWNY */}
                    <h3 className={style.SectionHeading}>Zakres usług</h3>
                    <div className={`${style.Row} ${style.ServicesRow}`}>
                      <div className={style.Field}>
                        <FormControlLabel
                          control={
                            <Switch
                              name="isADR"
                              checked={values.isADR === true}
                              onChange={(_, checked) => {
                                setFieldValue(
                                  "isADR",
                                  checked
                                );
                              }}
                            />
                          }
                          label="Przewóz materiałów niebezpiecznych ADR?"
                        />
                      </div>

                      <div className={style.Field}>
                        <FormControlLabel
                          control={
                            <Switch
                              name="isInternational"
                              checked={
                                values.isInternational === true
                              }
                              onChange={(_, checked) => {
                                setFieldValue(
                                  "isInternational",
                                  checked
                                );
                              }}
                            />
                          }
                          label="Transport międzynarodowy?"
                        />
                      </div>
                       <div className={style.Field}>
                        <FormControlLabel
                          control={
                            <Switch
                              name="isOversized"
                              checked={values.isOversized === true}
                              onChange={(_, checked) => {
                                setFieldValue("isOversized", checked);
                              }}
                            />
                          }
                          label="Transport ponadnormatywny?"
                        />
                      </div>
                    </div>

                    {/* ZAPIS */}
                    <div className={style.Button}>
                      <Button
                        variant="outlined"
                        disabled={!isValid}
                        type="submit"
                      >
                        ZAPISZ
                      </Button>
                    </div>

                  </div>
                </Form>
              </div>
            )}
          </Formik>

          {/* Logo i galerię zapisujemy osobno, poza formularzem Formik. */}
          <div className={style.MediaGrid}>
          <div className={`${style.Form} ${style.LogoSection}`}>
              <h3>Logo firmy</h3>
              <p className={style.GalleryInfo}>
                {packageRules.logo
                  ? `Pakiet ${packageRules.label}: logo widoczne w publicznej wizytówce.`
                  : "Pakiet Darmowy: logo pozostaje zapisane, ale nie jest wyświetlane publicznie."}
              </p>
              <div style={{ marginBottom: 12 }}>
                {logoPreview || logoUrl ? (
                  <img
                    src={
                      logoPreview ||
                      (logoUrl?.startsWith("http")
                        ? logoUrl
                        : `${process.env.REACT_APP_UPLOADS_URL || ""}${logoUrl}`)
                    }
                    alt="Podgląd logo firmy"
                    style={{ width: 180, height: 180, objectFit: "contain" }}
                  />
                ) : (
                  <div
                    style={{
                      width: 180,
                      height: 180,
                      border: "1px dashed #bbb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#999",
                      fontSize: 14,
                    }}
                  >
                    Brak logo firmy
                  </div>
                )}
              </div>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={logoBusy}
                onChange={(event) => {
                  setLogoFile(event.target.files?.[0] || null);
                  setLogoMessage("");
                }}
              />
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 12 }}>
                <Button variant="outlined" type="button" disabled={!logoFile || logoBusy} onClick={saveLogo}>
                  {logoBusy ? "Proszę czekać..." : "Zapisz logo"}
                </Button>
                <Button variant="outlined" color="error" type="button" disabled={!logoUrl || logoBusy} onClick={deleteLogo}>
                  Usuń logo
                </Button>
              </div>
              {logoMessage && <p role="status">{logoMessage}</p>}
              <p>Dozwolone pliki: JPG, PNG, WebP. Maksymalnie 10 MB.</p>
          </div>

          <div className={`${style.Form} ${style.GallerySection}`}>
            <div className={style.GalleryHeading}>
              <h3>Galeria zdjęć firmy</h3>
              <span className={style.PackageBadge}>Pakiet {packageRules.label}</span>
            </div>
            <p className={style.GalleryInfo}>
              Możesz zapisać maksymalnie 5 zdjęć. W pakiecie {packageRules.label}
              publicznie widocznych jest maksymalnie {visibleLimit} zdjęć.
              Pozostałe zdjęcia są ukryte. Zmień kolejność strzałkami,
              aby wybrać zdjęcia widoczne w wizytówce.
            </p>
            <p className={style.GalleryCounter}>
              Dodane zdjęcia: {photos.length}/5 · Widoczne: {visibleCount} ·
              Ukryte: {hiddenCount}
            </p>
            <div className={style.PhotoGrid}>
              {photos.map((photo, index) => (
                <div className={style.PhotoItem} key={photo}>
                  <div className={style.PhotoImageBox}>
                    <img src={getImageUrl(photo)} alt={`Zdjęcie firmy ${index + 1}`} />
                    <span className={`${style.PhotoStatus} ${index < visibleLimit ? style.PhotoVisible : style.PhotoHidden}`}>
                      {index < visibleLimit ? "Widoczne" : "Ukryte"}
                    </span>
                  </div>
                  <span className={style.PhotoPosition}>Pozycja {index + 1}</span>
                  <div className={style.PhotoControls}>
                    <Button size="small" variant="outlined" type="button"
                      aria-label={`Przesuń zdjęcie ${index + 1} w lewo`}
                      disabled={photoBusy || reordering || index === 0}
                      onClick={() => movePhoto(index, -1)}>←</Button>
                    <Button size="small" variant="outlined" type="button"
                      aria-label={`Przesuń zdjęcie ${index + 1} w prawo`}
                      disabled={photoBusy || reordering || index === photos.length - 1}
                      onClick={() => movePhoto(index, 1)}>→</Button>
                  </div>
                  <Button size="small" variant="outlined" color="error" type="button"
                    disabled={photoBusy || reordering} onClick={() => deletePhoto(photo)}>
                    Usuń zdjęcie
                  </Button>
                </div>
              ))}
              {photoPreviews.map((preview, index) => (
                <div className={style.PhotoItem} key={preview}>
                  <div className={style.PhotoImageBox}>
                    <img src={preview} alt={`Podgląd nowego zdjęcia ${index + 1}`} />
                  </div>
                  <span className={style.PhotoPosition}>Do zapisania</span>
                </div>
              ))}
            </div>
            {photos.length === 0 && photoFiles.length === 0 && (
              <p>Nie dodano jeszcze zdjęć firmy.</p>
            )}
            <input ref={photoInputRef} type="file" multiple
              accept="image/jpeg,image/png,image/webp"
              disabled={photoBusy || reordering || photos.length >= 5}
              onChange={(event) => choosePhotos(event.target.files)} />
            <div className={style.GalleryActions}>
              <Button variant="outlined" type="button" onClick={savePhotos}
                disabled={photoBusy || reordering || photoFiles.length === 0}>
                {photoBusy ? "Proszę czekać..." : "Zapisz zdjęcia"}
              </Button>
            </div>
            {photoMessage && <p role="status">{photoMessage}</p>}
            <p>Dozwolone: JPG, PNG, WebP. Maksymalnie 10 MB na zdjęcie.</p>
          </div>
          </div>
      </div>
    </PageLayout>
  );
};

export default Edit;
